// A flow record's columns, shared by a session's connections and the lookup.
import type { ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { RelativeTime } from '@/components/RelativeTime';
import { formatBytes, protocolName } from '@/features/vpn/format';
import type { FlowRecord } from './api';

/** "192.168.1.37:22", or just the address for ICMP. */
export function endpoint(address: string | null, port: number | null, protocol: number | null): string {
  if (!address) return '—';
  const host = address.includes(':') ? `[${address}]` : address;
  return port != null && protocol !== 1 && protocol !== 58 ? `${host}:${String(port)}` : address;
}

export const flowColumns: ResponsiveTableColumn<FlowRecord>[] = [
  { key: 'protocol', label: 'Protocol', render: (f) => protocolName(f.protocol) },
  {
    key: 'destination',
    label: 'Destination',
    hideMobileLabel: true,
    render: (f) => (
      <div className="min-w-0">
        <code className="text-xs">{endpoint(f.destinationReached, f.destinationPort, f.protocol)}</code>
        {f.destinationSent && f.destinationSent !== f.destinationReached ? (
          <div className="text-xs text-muted-foreground">sent to {f.destinationSent}</div>
        ) : null}
      </div>
    ),
  },
  {
    key: 'gateway',
    label: 'As the site saw it',
    render: (f) => (
      <code className="text-xs" title="The gateway's address and port after its masquerade: what the device logged">
        {endpoint(f.gatewayAddress, f.gatewayPort, f.protocol)}
      </code>
    ),
  },
  {
    key: 'bytes',
    label: 'From / to the client',
    align: 'right',
    hideAt: 'mobile',
    render: (f) => (
      <span className="tabular-nums">
        {formatBytes(f.bytesFromClient ?? NaN)} / {formatBytes(f.bytesToClient ?? NaN)}
      </span>
    ),
  },
  { key: 'start', label: 'Started', render: (f) => <RelativeTime value={f.start} /> },
  {
    key: 'end',
    label: 'Ended',
    hideAt: 'mobile',
    render: (f) => (f.ended ? <RelativeTime value={f.end} /> : <span className="text-muted-foreground">Open</span>),
  },
];
